/**
 * The planner's state on the client and how it maps onto the client order's fields (engine/clientKickoff.ts
 * CLIENT_SECTIONS). Nothing here prices anything: totals come from /planner/evaluate.
 */
import type { Block, BlockPlatform, HookCard, Insights, Prepared, PlannerAudience, Ref, ShapedConcept, Strategy } from './api';

export const STEPS = ['Setup', 'Goal', 'Insights', 'Creators', 'Audience', 'Concept', 'Media plan', 'Details', 'Checkout'] as const;
export type Step = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type CampaignType = 'influencer' | 'ugc';
export const FORMATS = ['9:16', '4:5', '1:1', '16:9'] as const;

/** The strategy as the client picked it (a snapshot: a rewrite never moves it), or the one Lykke matched their idea to. */
export interface StrategyPick extends Strategy { market: string; insightsAt: number | null; matched?: boolean }
/** What the concept stands on, and where it came from: shaped ahead from Insights, from their words, or Lykke's idea. */
export interface BuiltOn { strategyId: string | null; refs: Ref[]; from: 'ahead' | 'yours' | 'lykke' }

export interface PlannerState {
  // Setup
  product: string; market: string; language: string; type: CampaignType;
  // Goal
  objective: string | null;
  // Insights
  strategy: StrategyPick | null; ideaText: string;
  // Creators (the casting)
  platforms: BlockPlatform[]; band: string; niches: string[]; creatorGender: string; creatorAgeMin: string; creatorAgeMax: string;
  minEngagement: number; creatorDescription: string; preferred: string[]; avoid: string;
  // Audience
  audienceId: string | null; targetAudience: string; audienceAgeMin: string; audienceAgeMax: string; audienceGender: string;
  audienceCountry: string; audienceCountryPct: number; audienceInterests: string[];
  // Concept
  conceptTitle: string; hook: string; summary: string; keyMessage: string; idea: string; conceptId: string | null;
  builtOn: BuiltOn | null; gap: { ref: Ref; line: string } | null; gapSeen: string[];
  /** the Insights idea the concept was last shaped from (text, strategy, product, goal) */ shapedFrom: string | null;
  // Media plan
  /** what the plan was built from: budget and the choices that shape it; a change shows the rebuild call */
  budget: number; planBudget: number | null; planKey: string | null; currency: string; start: string; end: string; blocks: Block[]; blocksEdited: boolean;
  paidUsage: string; organicUsage: string;
  // Details
  campaignName: string; delivery: string; gift: string; link: string; exclusivity: string;
  approver: string; turnaround: string; contentUsage: string[];
  // Checkout
  agree: boolean; authority: boolean;
}

const one = (v: unknown) => (Array.isArray(v) ? String(v[0] ?? '') : String(v ?? ''));
const handlesOf = (v: unknown) => one(v).split(/[,\s]+/).map((h) => h.trim().replace(/^@?/, '@')).filter((h) => h.length > 1);

export function audiencePatch(a: PlannerAudience | undefined, market: string): Partial<PlannerState> {
  if (!a) return {};
  return {
    audienceId: a.id, targetAudience: `${a.name}${a.description ? `: ${a.description}` : ''}`,
    ...(a.ageMin ? { audienceAgeMin: String(a.ageMin) } : {}), ...(a.ageMax ? { audienceAgeMax: String(a.ageMax) } : {}),
    audienceGender: a.gender, audienceCountry: market, audienceInterests: a.interests.slice(0, 8),
  };
}

export function initialState(p: Prepared): PlannerState {
  const pre = p.prefill;
  const audience = p.audiences[0];
  return {
    product: p.brand.name, market: p.defaults.market, language: p.defaults.language, type: 'influencer', objective: null, strategy: null, ideaText: '',
    platforms: [], band: 'micro', niches: [], creatorGender: 'Undefined', creatorAgeMin: '', creatorAgeMax: '',
    minEngagement: 0, creatorDescription: '', preferred: handlesOf(pre.preferred_creators), avoid: '',
    audienceId: null, targetAudience: one(pre.target_audience), audienceAgeMin: one(pre.audience_age_min) || '25', audienceAgeMax: one(pre.audience_age_max) || '44',
    audienceGender: one(pre.audience_gender) || 'Any', audienceCountry: p.defaults.market, audienceCountryPct: 50, audienceInterests: [],
    ...audiencePatch(audience, p.defaults.market),
    conceptTitle: '', hook: '', summary: '', keyMessage: '', idea: '', conceptId: null, builtOn: null, gap: null, gapSeen: [], shapedFrom: null,
    budget: p.defaults.budget, planBudget: null, planKey: null, currency: p.defaults.currency, start: p.defaults.start, end: p.defaults.end, blocks: [], blocksEdited: false,
    paidUsage: one(pre.paid_usage_rights) || '3 months', organicUsage: one(pre.organic_usage_rights) || '12 months',
    campaignName: '', delivery: '', gift: '', link: one(pre.link), exclusivity: '3 months', approver: one(pre.approver_email), turnaround: '48 hours', contentUsage: [],
    agree: false, authority: false,
  };
}

/** Audience location, interests and engagement have no field of their own: plain words the sourcing agent reads. */
export function strategyLine(s: Pick<PlannerState, 'audienceCountry' | 'audienceCountryPct' | 'minEngagement' | 'audienceInterests'>, countryName: (id: string) => string): string {
  const parts: string[] = [];
  if (s.audienceCountry) parts.push(`At least ${s.audienceCountryPct}% of their audience in ${countryName(s.audienceCountry)}`);
  if (s.audienceInterests.length) parts.push(`an audience into ${s.audienceInterests.slice(0, 6).join(', ').toLowerCase()}`);
  if (s.minEngagement > 0) parts.push(`engagement rate at least ${s.minEngagement}%`);
  return parts.length ? `${parts.join('; ')}.` : '';
}

/** Every UGC video's format, in order (one per video the plan orders). */
export function ugcVideos(blocks: Block[]): Array<{ key: string; index: number; format: string }> {
  return blocks.filter((b) => b.kind === 'ugc').flatMap((b) => {
    const n = (b.creators || 0) * ((b.pieces?.VIDEO || 0) + (b.pieces?.PICTURE || 0));
    return Array.from({ length: Math.min(n, 40) }, (_, i) => ({ key: b.key, index: i, format: b.formats?.[i] || '9:16' }));
  });
}

/** The client order's fields from the planner (prefill first, the planner's answers on top). */
export function toValues(s: PlannerState, p: Prepared): Record<string, unknown> {
  const countryName = (id: string) => p.options.countries.find((c) => c.id === id)?.name || id;
  const posts = s.blocks.some((b) => b.kind === 'posts'); const ugc = s.blocks.some((b) => b.kind === 'ugc');
  const desc = [s.creatorDescription.trim(), strategyLine(s, countryName)].filter(Boolean).join('\n');
  const concept = [s.conceptTitle && `${s.conceptTitle}${s.hook ? `: "${s.hook}"` : ''}`, !s.conceptTitle && s.hook ? `Hook: "${s.hook}"` : '', theirWords(s), ...strategyText(s, countryName(s.market))].filter(Boolean).join('\n');
  const v: Record<string, unknown> = {
    ...p.prefill,
    campaign_name: s.campaignName.trim() || defaultName(s, p),
    main_objective: s.objective || '',
    summary: s.summary.trim(), key_message: s.keyMessage.trim(), creative_concept: concept,
    product_service: s.product.trim(), link: s.link.trim(),
    markets: [s.market], currency: s.currency, campaign_start: s.start, campaign_end: s.end,
    target_audience: s.targetAudience.trim(), audience_age_min: s.audienceAgeMin, audience_age_max: s.audienceAgeMax, audience_gender: s.audienceGender,
    creator_description: desc, creator_categories: s.niches, creator_gender: s.creatorGender, creator_age_min: s.creatorAgeMin, creator_age_max: s.creatorAgeMax,
    creator_language: [s.language], preferred_creators: s.preferred.join(', '), creators_to_avoid: s.avoid.trim(),
    product_delivery: s.delivery, products: s.gift.trim(),
    paid_usage_rights: s.paidUsage, organic_usage_rights: s.organicUsage, approver_email: s.approver.trim(), approval_turnaround: s.turnaround,
  };
  if (posts) v.competitor_clause = s.exclusivity;
  if (ugc) { v.content_usage = s.contentUsage; v.delivery_formats = [...new Set(ugcVideos(s.blocks).map((x) => x.format))]; }
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined && x !== null));
}
/** Their own words, once each: the Insights idea, then what they told Lykke on Concept. */
export const theirWords = (s: Pick<PlannerState, 'ideaText' | 'idea'>) => [...new Set([s.ideaText, ...s.idea.split('\n')].map((x) => x.trim()).filter(Boolean))].join('\n');

const REF_ROLE: Record<Ref['kind'], (r: Ref, market: string) => string> = {
  quote: (r) => `${r.label} (${r.tone === 'doubt' ? 'a customer doubt' : 'customers'})`, trend: (r, m) => `${r.label} (trend in ${m})`,
  hook: (r) => `${r.label} (a hook that worked for you)`, rival: (r) => `Unlike ${r.label} (the field)`, market: (r) => r.label,
};
/** The strategy into the order, so the campaign manager and the creator brief start from what the client saw. */
export function strategyText(s: Pick<PlannerState, 'strategy' | 'builtOn'>, market: string): string[] {
  if (!s.strategy) return [];
  const refs = [...s.strategy.refs, ...(s.builtOn?.refs || [])].filter((r, i, a) => a.findIndex((x) => x.id === r.id) === i);
  return [`Strategy: ${s.strategy.line}`, s.strategy.because && `Because: ${s.strategy.because}`, refs.length ? `Built on: ${refs.map((r) => REF_ROLE[r.kind](r, market)).join('; ')}` : ''].filter(Boolean) as string[];
}

/** A stable sort: the items `score` ranks higher first, the rest in their order. */
export const rankBy = <T,>(xs: T[], score: (x: T) => number): T[] => xs.map((x, i) => ({ x, i, k: score(x) })).sort((a, b) => b.k - a.k || a.i - b.i).map((y) => y.x);

const times = (n: number) => `${n >= 10 ? Math.round(n) : n.toFixed(1).replace(/\.0$/, '')}×`;
/** A hook's number, as a multiple of the brand's own usual: lift and reach are stored as the share above it. */
export function hookStat(h: Pick<HookCard, 'kpi' | 'lift' | 'reach'>): string {
  if (h.kpi === 'paid_hook_rate' && typeof h.lift === 'number' && h.lift > 0) return `${times(1 + h.lift)} your usual hook rate`;
  if (typeof h.reach === 'number' && h.reach > 0) return `${times(1 + h.reach)} your usual reach`;
  if (typeof h.lift === 'number' && h.lift > 0) return `${times(1 + h.lift)} your usual hook rate`;
  return 'Worked for you';
}

/** The shaping call's form, for the composer and for the shaping ahead: every send carries the concept as it stands. */
export function conceptForm(s: PlannerState, p: Prepared, o: { text: string; add?: string; fresh?: boolean; files?: Array<{ file: File; name: string }> }): FormData {
  const f = new FormData();
  const has = !o.fresh && !!(s.summary || s.conceptTitle);
  const fields: Record<string, string> = {
    brandId: p.brand.id, text: o.text, market: s.market, objective: s.objective || '', product: s.product, audience: s.targetAudience.slice(0, 300),
    strategy: s.strategy && !s.strategy.matched ? JSON.stringify({ id: s.strategy.id, line: s.strategy.line, because: s.strategy.because, refs: s.strategy.refs }) : '',
    current: has ? JSON.stringify({ title: s.conceptTitle, hook: s.hook, summary: s.summary, keyMessage: s.keyMessage }) : '',
    earlier: o.fresh ? '' : theirWords(s).slice(0, 1500), add: o.add || '', left: JSON.stringify(s.gapSeen),
  };
  Object.entries(fields).forEach(([k, v]) => f.append(k, v));
  (o.files || []).forEach((x) => f.append('files', x.file, x.name));
  return f;
}
export const shapeKey = (s: Pick<PlannerState, 'ideaText' | 'strategy' | 'product' | 'objective'>) => JSON.stringify([s.ideaText.trim(), s.strategy && !s.strategy.matched ? s.strategy.id : null, s.product, s.objective]);

/**
 * A shaped concept into the state: the card, what it is built on, one thing it could still use (never one left out), and,
 * when nothing was picked, the strategy Lykke matched it to.
 */
export function shapedPatch(s: PlannerState, c: ShapedConcept, o: { from: BuiltOn['from']; text: string; insights: Insights | null }): Partial<PlannerState> {
  const st = c.strategyId ? (o.insights?.strategies || []).find((x) => x.id === c.strategyId) : undefined;
  const matched = (!s.strategy || s.strategy.matched) && st ? { strategy: { ...st, market: s.market, insightsAt: o.insights?.at ?? null, matched: true } } : {};
  return {
    conceptTitle: c.title, hook: c.hook, summary: c.summary, keyMessage: c.keyMessage, conceptId: null,
    idea: o.from === 'ahead' ? s.idea : [s.idea, o.text].filter(Boolean).join('\n').slice(0, 1500),
    builtOn: { strategyId: c.strategyId, refs: c.refs, from: o.from },
    gap: c.gap && !s.gapSeen.includes(c.gap.ref.id) ? c.gap : null, ...matched,
  };
}

// ── Lykke beside the planner (PlannerLykke.tsx): the plan as she reads it, and her changes into the state ──

const LYKKE_KEYS = ['product', 'market', 'language', 'type', 'objective', 'ideaText', 'platforms', 'band', 'niches', 'creatorGender', 'creatorAgeMin', 'creatorAgeMax', 'minEngagement', 'creatorDescription', 'preferred', 'avoid',
  'targetAudience', 'audienceAgeMin', 'audienceAgeMax', 'audienceGender', 'conceptTitle', 'hook', 'summary', 'budget', 'start', 'end', 'paidUsage', 'organicUsage', 'campaignName', 'delivery', 'gift', 'link', 'exclusivity', 'approver', 'turnaround', 'contentUsage'] as const;
/** The plan as Lykke reads it: the planner's own keys, the strategy as its number on Insights, the audience by name. */
export function lykkeView(s: PlannerState, p: Prepared, insights: Insights | null): Record<string, unknown> {
  const v: Record<string, unknown> = Object.fromEntries(LYKKE_KEYS.map((k) => [k, s[k]]));
  const i = s.strategy ? (insights?.strategies || []).findIndex((x) => x.id === s.strategy!.id) : -1;
  v.strategy = i >= 0 ? String(i + 1) : s.strategy?.line || '';
  v.audience = p.audiences.find((a) => a.id === s.audienceId)?.name || '';
  return v;
}
/** Lykke's changes (planner keys, the strategy's number, an audience by name) as a patch of the state. */
export function lykkePatch(x: Record<string, unknown>, s: PlannerState, p: Prepared, insights: Insights | null): Partial<PlannerState> {
  const out: Record<string, unknown> = {};
  const marketMoves = typeof x.market === 'string' && x.market !== s.market;
  for (const [k, v] of Object.entries(x)) {
    if (k === 'strategy') { const st = insights?.strategies?.[Number(v) - 1]; if (st && !marketMoves) out.strategy = { ...st, market: s.market, insightsAt: insights?.at ?? null }; }
    else if (k === 'audience') Object.assign(out, audiencePatch(p.audiences.find((a) => a.name === v), s.market));
    else if (k === 'market') { out.market = v; out.audienceCountry = v; if (marketMoves) out.strategy = null; }
    else if (k === 'type') { out.type = v; out.objective = v === 'ugc' ? 'Content production (UGC)' : s.objective === 'Content production (UGC)' ? null : s.objective; }
    else if (k === 'creatorAgeMin' || k === 'creatorAgeMax' || k === 'audienceAgeMin' || k === 'audienceAgeMax') out[k] = String(v);
    else if ((LYKKE_KEYS as readonly string[]).includes(k)) out[k] = v;
  }
  if ('objective' in x && !('type' in x)) out.type = x.objective === 'Content production (UGC)' ? 'ugc' : 'influencer';
  return out as Partial<PlannerState>;
}
/** What changes the media plan: a Lykke change here rebuilds it (unless the client edited the plan by hand). */
export const PLAN_KEYS = ['budget', 'type', 'objective', 'platforms', 'band', 'audience', 'audienceAgeMin', 'audienceAgeMax', 'paidUsage'];

/** A draft from before strategies: the free strategy text was their idea. */
export function migrateDraft<T extends { s: Record<string, unknown> }>(d: T): T {
  const x = { ...d.s };
  if (typeof x.strategy === 'string') { x.ideaText = x.strategy; x.strategy = null; }
  return { ...d, s: x };
}

export const defaultName = (s: Pick<PlannerState, 'product' | 'start'>, p: Prepared) => {
  const when = s.start ? new Date(`${s.start}T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';
  return [s.product || p.brand.name, when].filter(Boolean).join(', ');
};

/** Which step answers a missing field. */
export const STEP_OF_FIELD: Record<string, Step> = {
  product_service: 0, markets: 0, creator_language: 0, main_objective: 1,
  creator_description: 3, creator_categories: 3,
  target_audience: 4, audience_age_min: 4, audience_age_max: 4, audience_gender: 4,
  summary: 5, key_message: 5, creative_concept: 5,
  campaign_start: 6, campaign_end: 6, currency: 6, blocks: 6, paid_usage_rights: 6, organic_usage_rights: 6,
  campaign_name: 7, product_delivery: 7, approver_email: 7, competitor_clause: 7, content_usage: 7, delivery_formats: 7, link: 7,
};
export const stepOfGap = (id: string): Step => (id.startsWith('block:') ? 6 : STEP_OF_FIELD[id] ?? 7);

/** The choices a plan is built from (a change shows the rebuild call instead of rebuilding under the client). */
export const planKeyOf = (s: PlannerState) => JSON.stringify([s.type, s.objective, s.budget, s.currency, s.platforms, s.band, s.audienceAgeMin, s.audienceAgeMax, s.paidUsage]);

export const fmt = (n: number) => Math.round(n).toLocaleString('sv-SE').replace(/\s/g, ' ');
export const short = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace('.0', '')}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(Math.round(n)));
export const day = (iso: string) => (iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '');
